import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, ExternalLink, Package, Calendar, DollarSign, 
  Truck, MapPin, ClipboardCheck, PackageCheck, FileText
} from "lucide-react";
import { toast } from 'sonner';

import { API } from '@/config';

export default function SalesOrderDetailPage() {
  const { soId } = useParams();
  const navigate = useNavigate();
  const [so, setSo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSO();
  }, [soId]);

  const fetchSO = async () => {
    try {
      const response = await axios.get(`${API}/warehouse/sales-orders/${soId}`);
      setSo(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching SO:', error);
      toast.error('Failed to load sales order');
      setLoading(false);
    }
  };

  const openNetSuite = () => {
    if (so?.netsuite_id) {
      window.open(`https://system.netsuite.com/app/accounting/transactions/salesord.nl?id=${so.netsuite_id}`, '_blank');
      toast.success('Opening NetSuite record...');
    }
  };

  const handleSchedulePickup = () => {
    navigate('/warehouse/trucks', { state: { soNumber: so.so_number } });
  };

  const handleStartPicking = () => {
    toast.success('Starting pick process...');
    // Navigate to picking workflow
  };

  const handleGenerateBOL = () => {
    toast.success('Generating Bill of Lading...');
    // Generate BOL document
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      partially_picked: 'bg-yellow-100 text-yellow-700',
      picked: 'bg-purple-100 text-purple-700',
      partially_shipped: 'bg-orange-100 text-orange-700',
      shipped: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-700'
    };
    return (
      <Badge className={`${styles[status] || 'bg-gray-100 text-gray-700'} text-sm`}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!so) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">Sales order not found</p>
          <Button onClick={() => navigate('/sales-orders')} className="mt-4">
            Back to Sales Orders
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/sales-orders')}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{so.so_number}</h1>
              {getStatusBadge(so.status)}
            </div>
            <p className="text-gray-600">Sales Order Details</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openNetSuite} className="gap-2">
            <ExternalLink className="w-4 h-4" />
            Open in NetSuite
          </Button>
          {so.status === 'open' && (
            <>
              <Button variant="outline" onClick={handleSchedulePickup} className="gap-2">
                <Truck className="w-4 h-4" />
                Schedule Pickup
              </Button>
              <Button onClick={handleStartPicking} className="gap-2 bg-blue-600 hover:bg-blue-700">
                <PackageCheck className="w-4 h-4" />
                Start Picking
              </Button>
            </>
          )}
          {(so.status === 'picked' || so.status === 'partially_picked') && (
            <Button onClick={handleGenerateBOL} className="gap-2 bg-green-600 hover:bg-green-700">
              <FileText className="w-4 h-4" />
              Generate BOL
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="col-span-2 space-y-6">
          {/* SO Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5" />
                Order Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-500">Customer</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Package className="w-4 h-4 text-gray-400" />
                    <p className="font-medium text-gray-900">{so.customer}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Customer ID</label>
                  <p className="font-medium text-gray-900 mt-1">{so.customer_id || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Order Date</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <p className="font-medium text-gray-900">{so.order_date}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Requested Ship Date</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="w-4 h-4 text-blue-400" />
                    <p className="font-medium text-gray-900">{so.requested_ship_date}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Total Amount</label>
                  <div className="flex items-center gap-2 mt-1">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    <p className="font-bold text-gray-900 text-lg">
                      ${so.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">NetSuite ID</label>
                  <p className="font-medium text-gray-900 mt-1">{so.netsuite_id}</p>
                </div>
              </div>
              {so.shipping_address && (
                <div>
                  <label className="text-sm text-gray-500 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Shipping Address
                  </label>
                  <p className="font-medium text-gray-900 mt-1 whitespace-pre-line">{so.shipping_address}</p>
                </div>
              )}
              {so.notes && (
                <div>
                  <label className="text-sm text-gray-500">Notes</label>
                  <p className="text-gray-900 mt-1">{so.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Line Items ({so.line_items.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Line</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ordered</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Picked</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Shipped</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Remaining</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {so.line_items.map((item) => (
                      <tr key={item.line_number} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm text-gray-900">{item.line_number}</td>
                        <td className="px-3 py-3 text-sm text-gray-900">{item.item_name}</td>
                        <td className="px-3 py-3 text-sm text-gray-600">{item.sku}</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-900">
                          {item.quantity_ordered} {item.unit}
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-purple-600 font-medium">
                          {item.quantity_picked} {item.unit}
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-green-600 font-medium">
                          {item.quantity_shipped} {item.unit}
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-blue-600 font-medium">
                          {item.quantity_remaining} {item.unit}
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-900">
                          ${item.unit_price.toFixed(2)}
                        </td>
                        <td className="px-3 py-3 text-sm text-right font-medium text-gray-900">
                          ${item.total_price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Summary & Actions */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="text-sm text-gray-500">Total Items</div>
                <div className="text-2xl font-bold text-gray-900">{so.line_items.length}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Total Ordered</div>
                <div className="text-2xl font-bold text-gray-900">
                  {so.line_items.reduce((sum, item) => sum + item.quantity_ordered, 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Total Picked</div>
                <div className="text-2xl font-bold text-purple-600">
                  {so.line_items.reduce((sum, item) => sum + item.quantity_picked, 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Total Shipped</div>
                <div className="text-2xl font-bold text-green-600">
                  {so.line_items.reduce((sum, item) => sum + item.quantity_shipped, 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Completion</div>
                <div className="text-2xl font-bold text-blue-600">
                  {(() => {
                    const totalOrdered = so.line_items.reduce((sum, item) => sum + item.quantity_ordered, 0);
                    const totalShipped = so.line_items.reduce((sum, item) => sum + item.quantity_shipped, 0);
                    return totalOrdered > 0 ? Math.round((totalShipped / totalOrdered) * 100) : 0;
                  })()}%
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Linked Trucks */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Linked Trucks ({so.linked_truck_ids.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {so.linked_truck_ids.length > 0 ? (
                <div className="space-y-2">
                  {so.linked_truck_ids.map((truckId, idx) => (
                    <div key={idx} className="text-sm text-gray-600">
                      Truck #{idx + 1}
                    </div>
                  ))}
                  <Button 
                    variant="outline" 
                    className="w-full mt-2"
                    onClick={() => navigate('/warehouse/trucks')}
                  >
                    View Truck Schedule
                  </Button>
                </div>
              ) : (
                <div className="text-sm text-gray-500 text-center py-4">
                  No trucks scheduled yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
